import { Injectable } from '@angular/core';
import { PassService } from '@core/services/pass/pass.service';
import { BehaviorSubject, Observable } from 'rxjs';

interface MenuPrices {
    products: {};
    product_sizes: {};
    product_customizations: {};
}

@Injectable({
    providedIn: 'root'
})
export class PriceService {
    menu: any;

    private _prices = new BehaviorSubject<MenuPrices>(null);
    private _prices$ = this._prices.asObservable();

    constructor(private passService: PassService) {
        this.passService.customerPassDetails$.subscribe(response => {
            if (response && Object.keys(response)) {
                this.passService.generatePassModel();
                this.calculateMenuPrices();
            }
        });
    }

    setMenu(menu) {
        // set menu and calculate price
        this.menu = menu;
        this.passService.setMenu(menu);
    }

    getMenuPrices(): Observable<MenuPrices> {
        return this._prices$;
    }

    calculateMenuPrices() {
        if (!this.menu || !Object.keys(this.menu).length) {
            return {};
        }

        const { products, product_sizes, product_customizations } = this.menu;

        const prices: MenuPrices = {
            products: {},
            product_sizes: {},
            product_customizations: {}
        };

        Object.values(product_customizations).forEach((customization: any) => {
            const { price, marked_price } = customization;
            prices.product_customizations[customization.id] = {
                price,
                marked_price
            };
        });

        Object.values(product_sizes).forEach((size: any) => {
            const { price, marked_price } = this.getProductSizePrice(size);
            prices.product_sizes[size.id] = {
                price,
                marked_price
            };
        });

        Object.values(products).forEach((product: any) => {
            const { id, product_sizes } = product;
            const { price, marked_price } = this.getProductPrice(product_sizes);
            prices.products[id] = {
                price,
                marked_price
            };
        });

        this._prices.next(prices);
    }

    getPassPrice(product_size: any) {
        const active_pass = this.passService.getActivePass();

        if (!active_pass) {
            return null;
        }

        if (
            !product_size ||
            !product_size.prices ||
            !Object.keys(product_size.prices).length ||
            !product_size.prices.subscription_prices
        ) {
            return null;
        }
        return product_size.prices.subscription_prices[0]?.price ?? null;
    }

    getProductPrice(product_sizes: any): any {
        // default size price
        const size = this.getDefaultSize(product_sizes);
        if (!size) {
            return {};
        }
        return this.getProductSizePrice(size);
    }

    getDefaultSize(sizes) {
        if (!sizes) {
            return null;
        }

        const defaultSize = sizes.find(size => size.dflt);

        if (defaultSize) {
            return defaultSize;
        }

        for (const size of sizes) {
            if (!size.sold_out) {
                return size;
            }
        }

        return sizes[0];
    }

    getProductSizePrice(size: any) {
        let price = size.price;
        let marked_price = size.marked_price;
        const pass_price = this.getPassPrice(size);

        price = pass_price || price;
        marked_price = marked_price ? (marked_price > price ? marked_price : null) : null;
        return { price, marked_price };
    }

    // Cart prices
    getCartItemMarkedPrice(menuData, productSizeId, customizationString) {
        const { product_sizes, product_customizations } = menuData;

        const productSize = product_sizes[productSizeId];
        const productSizeMarkedPrice = productSize.marked_price;

        const productCustomizationsMarkedPrice = customizationString
            .split('-')
            .filter(Boolean)
            .reduce((total, customizationId) => {
                if (product_customizations[customizationId]) {
                    return total + product_customizations[customizationId].marked_price;
                } else {
                    return total;
                }
            }, 0);
        return productSizeMarkedPrice + productCustomizationsMarkedPrice;
    }

    getCartItemPrice(menuData, productSizeId, customizationString) {
        const { product_sizes, product_customizations } = menuData;

        const productSize = product_sizes[productSizeId];
        const productSizePrice = productSize && productSize.price;
        const passPrice = this.getPassPrice(productSize);

        const productCustomizationsPrice = customizationString
            .split('-')
            .filter(Boolean)
            .reduce((total, customizationId) => {
                if (product_customizations[customizationId]) {
                    return total + product_customizations[customizationId].price;
                } else {
                    return total;
                }
            }, 0);

        return (passPrice || productSizePrice) + productCustomizationsPrice;
    }
}
